/**
 * ImToIM Client - Core SDK class
 */

import { EventEmitter } from './events.js';
import { IdentityManager } from '../identity/manager.js';
import { MessageService } from '../messages/service.js';
import { NetworkManager } from '../network/manager.js';
import { StorageManager } from '../storage/manager.js';

export class ImToIMClient extends EventEmitter {
  constructor(config = {}) {
    super();

    // 自动检测协议（浏览器环境）
    const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
    const defaultProtocol = isHttps ? 'https' : 'http';
    const defaultWsProtocol = isHttps ? 'wss' : 'ws';
    const defaultHost = typeof window !== 'undefined' ? window.location.host : 'localhost:18080';

    this.config = {
      serverUrl: config.serverUrl || `${defaultWsProtocol}://${defaultHost}`,
      apiUrl: config.apiUrl || `${defaultProtocol}://${defaultHost}`,
      reconnectInterval: config.reconnectInterval || 5000,
      heartbeatInterval: config.heartbeatInterval || 30000,
      storage: config.storage || 'localStorage',
      debug: config.debug || false,
    };

    this.state = {
      connected: false,
      authenticated: false,
      connecting: false,
      userId: null,
      token: null,
    };

    this.identity = new IdentityManager(this);
    this.messages = new MessageService(this);
    this.network = new NetworkManager(this);
    this.storage = new StorageManager(this.config.storage);

    this._messageQueue = [];
    this._initialized = false;
  }

  /**
   * Initialize the SDK
   */
  async initialize() {
    if (this._initialized) return this;

    this._log('Initializing ImToIM SDK...');

    // Initialize storage
    await this.storage.initialize();

    // Try to restore session
    const session = await this.storage.get('session');
    if (session) {
      this.state.userId = session.userId;
      this.state.token = session.token;
    }

    // Initialize identity
    await this.identity.initialize();

    this._initialized = true;
    this.emit('initialized');

    return this;
  }

  /**
   * Connect to server
   */
  async connect() {
    if (this.state.connected || this.state.connecting) return;

    this.state.connecting = true;
    this.emit('connecting');

    try {
      await this.network.connect(this.config.serverUrl);
      this.state.connected = true;
      this.state.connecting = false;
      this.emit('connected');

      // Auto-authenticate if we have a token
      if (this.state.token) {
        await this.authenticate(this.state.token);
      }

      // Process queued messages
      this._processMessageQueue();

    } catch (error) {
      this.state.connecting = false;
      this.emit('error', error);
      this._scheduleReconnect();
    }
  }

  /**
   * Disconnect from server
   */
  async disconnect() {
    this.state.connected = false;
    this.state.authenticated = false;
    await this.network.disconnect();
    this.emit('disconnected');
  }

  /**
   * Register a new user
   */
  async register(username) {
    const result = await this.identity.register(username);

    if (result.success) {
      this.state.userId = result.userId;
      this.state.token = result.token;

      // Save session
      await this.storage.set('session', {
        userId: result.userId,
        token: result.token,
      });

      this.emit('registered', result);
    }

    return result;
  }

  /**
   * Authenticate with userId and token
   */
  async authenticate(userId, token) {
    this.state.userId = userId;
    try {
      const result = await this.network.authenticate(userId, token);

      if (result.success) {
        this.state.authenticated = true;
        this.state.token = token;

        await this.storage.set('session', {
          userId: this.state.userId,
          token: token,
        });

        this.emit('authenticated', result);
      }

      return result;
    } catch (error) {
      this.emit('authError', error);
      throw error;
    }
  }

  /**
   * Logout
   */
  async logout() {
    this.state.authenticated = false;
    this.state.userId = null;
    this.state.token = null;

    await this.storage.remove('session');
    await this.disconnect();

    this.emit('logout');
  }

  /**
   * Send a message
   */
  async sendMessage(recipientId, content, options = {}) {
    return this.messages.send(recipientId, content, options);
  }

  /**
   * Get conversation history
   */
  async getHistory(options = {}) {
    return this.messages.getHistory(options);
  }

  /**
   * Get user info
   */
  async getUserInfo(userId) {
    return this.network.apiGet(`/api/v1/users/${userId}`);
  }

  /**
   * Check connection state
   */
  isConnected() {
    return this.state.connected;
  }

  /**
   * Check if authenticated
   */
  isAuthenticated() {
    return this.state.authenticated;
  }

  /**
   * Get current user ID
   */
  getUserId() {
    return this.state.userId;
  }

  _log(...args) {
    if (this.config.debug) {
      console.log('[OpenIM]', ...args);
    }
  }

  _scheduleReconnect() {
    setTimeout(() => {
      if (!this.state.connected) {
        this.connect();
      }
    }, this.config.reconnectInterval);
  }

  _processMessageQueue() {
    while (this._messageQueue.length > 0) {
      const msg = this._messageQueue.shift();
      this.network.send(msg);
    }
  }
}
